/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */
// baki

import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import Alert from 'react-s-alert';
import moment from 'moment';
import Calendar from 'react-calendar';
import { Input } from 'reactstrap';
import Joyride, { STATUS } from 'react-joyride';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import AuthService from '../../services/AuthService';
import ProviderService from '../../services/ProviderService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';
import FullPageLoader from '../general/FullPageLoader';
import ProviderNavbar from './ProviderNavbar';
import SelectedClient from './SelectedClient';
import UserNotApproved from './UserNotApproved';

import Message from '../general/Message';

const ReserveSession = props => {

    // contexts
    const { theme, fontSize, fontFamily } = useContext(ThemeContext);
    const { user, setUser, userApprovalStatus, selectedTimeZone } = useContext(AuthContext);

    // states
    const [selectedDate, setSelectedDate] = useState(moment().add(1, 'day').utcOffset("+05:30").toDate());
    const [isLoading, setIsLoading] = useState(false);
    const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);
    const [includeReport, setIncludeReport] = useState(false);
    const [providerDetail, setProviderDetail] = useState({ email: "", availableTimings: [], modeOfService: { call: {}, chat: {}, video: {}, inPerson: {} }, reportCharge: 0, GSTCharge: 0 });
    const [selectedClientIndex, setSelectedClientIndex] = useState(0);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [listOfAvailableDaysAndBookings, setListOfAvailableDaysAndBookings] = useState([]);
    const [listOfDateAndSlots, setListOfDateAndSlots] = useState([]);
    const [selectedSlotIndex, setSelectedSlotIndex] = useState(-1);
    const [timeSlotSize, setTimeSlotSize] = useState(0);
    const [listOfSlots, setListOfSlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState([]);
    const [selectedModeOfService, setSelectedModeOfService] = useState(null);
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    const [clientSubscription, setClientSubscription] = useState({ isActive: false, isCallSessionAvailable: false, isVideoSessionAvailable: false, isInPersonSessionAvailable: false, reportStatus: false });
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: ['Looks like your profile is incomplete! Please complete that by going to ', <b><Link to='/counsellor/profile'>Profile</Link></b>, ' page to enjoy our service.'],
            msgError: true
        }
    });
    const [isFirstSession, setIsFirstSession] = useState({
        status: false,
        message: {
            msgBody: "Your first session at Umang Wellness is free for 30 minutes, and only phone call is offered during first session",
            msgError: false
        }
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>You can book a new session from here.</h2>,
                placement: 'center',
                target: '#bookSessionDiv',
            },
            {
                content: <div className="tooltipDiv">Select a date for the session</div>,
                placement: 'right',
                target: '#selectDate'
            },
            {
                content: <div className="tooltipDiv">Select the time slot(s)<br />You can select a maximum 2 adjacent slots.</div>,
                placement: 'right',
                target: '#slotList'
            },
            {
                content: <div className="tooltipDiv">Select the mode of service you prefer.<br />The prices may vary depending on your choice of counsellor.</div>,
                placement: 'top',
                target: '#selectMode'
            },
            {
                content: <div className="tooltipDiv">Select if you wish to have a session report from your counsellor at the end of all the sessions.<br />There will be a one-time charge for the session report.</div>,
                placement: 'top',
                target: '#sessionReport'
            },
            {
                content: <div className="tooltipDiv">Click here to proceed for payment</div>,
                placement: 'top',
                target: '#saveBtn'
            }
        ]
    });
    let helpers = null;

    // useEffects
    useEffect(() => {
        ProviderService.getClients()
            .then(clientsData => {
                // console.log('clients', clientsData);
                setClients(clientsData.clients);
                ProviderService.getProviderDetails()
                    .then(providerData => {
                        setProfileComplete({ ...profileComplete, status: providerData.profileComplete });
                        setProviderDetail({
                            email: providerData.email,
                            modeOfService: providerData.modeOfService,
                            reportCharge: providerData.reportCharge,
                            GSTCharge: providerData.GSTCharge
                        });
                        ProviderService.getAvailableDaysAndBookings()
                            .then(availableDaysAndBookingsData => {
                                try {
                                    setMinMaxDates(availableDaysAndBookingsData.listOfAvailableDaysAndBookings);
                                    setTimeSlotSize(availableDaysAndBookingsData.timeSlotSize);
                                    setListOfAvailableDaysAndBookings(availableDaysAndBookingsData.listOfAvailableDaysAndBookings);
                                } catch (err) {
                                    Alert.error("Some error occured, please try again later or report support team.", {
                                        position: 'top-right',
                                        effect: 'slide'
                                    });
                                    GeneralService.logError('at Client BookSession useEffect2_2', 'Error in try catch block', err.stack);
                                }
                                setLoading(false);
                            });
                    });
            });
    }, [])

    useEffect(() => {
        if (minDate && maxDate) {
            setMinMaxDates();
        }
    }, [selectedTimeZone.offset]);

    useEffect(() => {
        if (listOfDateAndSlots.length > 0)
            updateDateIndex(selectedDate);
    }, [listOfDateAndSlots]);

    const setMinMaxDates = (list) => {
        let tempListOfAvailableDaysAndBookings = [];
        if (list) {
            tempListOfAvailableDaysAndBookings = list;
        } else {
            tempListOfAvailableDaysAndBookings = listOfAvailableDaysAndBookings;
        }
        createSlots(tempListOfAvailableDaysAndBookings);
        let startDate = moment(tempListOfAvailableDaysAndBookings[0].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset);
        let endDate = moment(tempListOfAvailableDaysAndBookings[tempListOfAvailableDaysAndBookings.length - 1].to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset);
        // console.log("startDate", startDate.format("DD-MM-YYYY HH:mm"));
        // console.log("endDate", endDate.format("DD-MM-YYYY HH:mm"));
        startDate = new Date(startDate.get('year'), startDate.get('month'), startDate.get('date'));
        endDate = new Date(endDate.get('year'), endDate.get('month'), endDate.get('date'));
        setMinDate(startDate);
        setMaxDate(endDate);
        setSelectedDate(startDate);
    }

    const updateDateIndex = (e) => {
        const date = moment(e).format("DD-MM-YYYY");
        const dateIndex = listOfDateAndSlots.findIndex(item => date === moment(item[1].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY"));
        setSelectedSlotIndex(dateIndex);
    }

    // handle functions
    const handleDatePickerChange = e => {
        try {
            setSelectedDate(e);
            updateDateIndex(e);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession handleDatePickerChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSelectClient = (index) => {
        setSelectedClientIndex(index);
        if (index > 0 && clients[index - 1].clientId.sessionList.length === 0) {
            setIncludeReport(false);
            selectModeOfService(providerDetail.modeOfService.call);
            setIsFirstSession({ ...isFirstSession, status: true })
        }
    }

    const handleSubmit = e => {
        try {
            if (!profileComplete.status) {
                Alert.error("Please complete your profile first", {
                    position: 'top-right',
                    effect: 'slide'
                });
            } else if (selectedClientIndex < 1) {
                Alert.error("Please select a client", {
                    position: 'top-right',
                    effect: 'slide'
                });
            } else {
                if (selectedDate == null) {
                    Alert.error("Please select date", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                }
                if (selectedTimeSlot.length < 1) {
                    Alert.error("Please select timeslot", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                }
                if (selectedModeOfService == null) {
                    Alert.error("Please select mode of service", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                }
                if (selectedDate != null && selectedTimeSlot.length != 0 && selectedModeOfService != null) {
                    setDisableSubmitBtn(true);
                    const timeSlots = selectedTimeSlot.length > 1 ? [listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[0]], listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[1]]] : [listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[0]]];
                    setUser({
                        ...user, bookSessionDetails: {
                            selectedDate,
                            selectedTimeSlot,
                            selectedModeOfService
                        }
                    });

                    let tempDate = moment(selectedDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({
                        hour: moment(timeSlots[0].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).get('hour'),
                        minute: moment(timeSlots[0].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).get('minute')
                    }).toISOString();

                    let tempTimeSlot = timeSlots.map(item => {
                        return { ...item, isLocked: true, lockedAt: moment().utcOffset(0).toISOString(), lockedBy: user.email }
                    });

                    props.history.push({
                        pathname: "/counsellor/confirmsession",
                        state: {
                            providerDetail: providerDetail,
                            selectedClient: clients[selectedClientIndex - 1],
                            selectedDate: tempDate,
                            selectedTimeSlot: tempTimeSlot,
                            modeOfServiceSelected: selectedModeOfService,
                            isFirstSession: isFirstSession.status,
                            isReportIncluded: {
                                status: includeReport,
                                amount: includeReport ? providerDetail.reportCharge : 0
                            }
                        }
                    }).catch(error => {
                        Alert.error(error.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    });

                }
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession handleSubmit', 'Error in try catch block', err.stack);
        }
    }

    // utility functions
    // const setSlots = (size, timings) => {
    //     try {
    //         timings.map((item, index) => {
    //             let tempFrom = moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 });
    //             let tempTo = moment(item.to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 });
    //             let slots = listOfSlots;
    //             slots.push({ isExtra: true, value: index + 1 });
    //             while (!tempFrom.isSame(tempTo)) {
    //                 let slot = { from: "", to: "" };
    //                 slot.from = tempFrom.toISOString();
    //                 tempFrom = moment(tempFrom).set({ second: 0, millisecond: 0 }).add(size, 'minutes');
    //                 slot.to = tempFrom.toISOString();
    //                 slots.push(slot);
    //             }
    //             setListOfSlots(slots);
    //         })
    //     } catch (err) {
    //         Alert.error("Some error occured, please try again later or report support team.", {
    //             position: 'top-right',
    //             effect: 'slide'
    //         });
    //         GeneralService.logError('at Client BookSession setSlots', 'Error in try catch block', err.stack);
    //     }
    // }

    const toggleIncludeReport = e => {
        try {
            setIncludeReport(!includeReport);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession toggleIncludeReport', 'Error in try catch block', err.stack);
        }
    }

    const selectTimeSlot = (index) => {
        try {
            if (selectedTimeSlot.includes(index)) {
                setSelectedTimeSlot(selectedTimeSlot.filter(item => item !== index));
            } else if (selectedTimeSlot.length < 2) {
                if (selectedTimeSlot.length == 0) {
                    setSelectedTimeSlot([index]);
                } else {
                    if (isFirstSession.status && selectedTimeSlot.length === 1) {
                        setSelectedTimeSlot([index]);
                        // Alert.error(`Please select only 1 timeslot`, {
                        //     position: 'top-right',
                        //     effect: 'slide'
                        // });
                        return;
                    }
                    if (Math.abs(moment(listOfDateAndSlots[selectedSlotIndex][index].from).diff(moment(listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[0]].from), 'minutes')) === timeSlotSize) {
                        if (selectedTimeSlot[0] < index) {
                            setSelectedTimeSlot([...selectedTimeSlot, index]);
                        } else {
                            setSelectedTimeSlot([index, ...selectedTimeSlot]);
                        }
                    } else {
                        Alert.error("Must select consecutive slots", {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    }
                }
            } else {
                Alert.error("Cannot select more than 2 slots", {
                    position: 'top-right',
                    effect: 'slide'
                });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession selectTimeSlot', 'Error in try catch block', err.stack);
        }
    }

    const selectModeOfService = (mode) => {
        try {
            setSelectedModeOfService(mode);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession selectModeOfService', 'Error in try catch block', err.stack);
        }
    }

    // const isAvailableTimeSlot = (slot) => {
    //     try {
    //         // const dateIndex = listOfAvailableDaysAndBookings.findIndex(x => moment(x.date).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY") === moment(selectedDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY"));
    //         let dateIndex = listOfAvailableDaysAndBookings.findIndex(x => moment(x.date).format("DD-MM-YYYY") === moment(selectedDate).utcOffset("+05:30").format("DD-MM-YYYY"));
    //         // console.log(moment(selectedDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY"), dateIndex);
    //         const bookedTimings = listOfAvailableDaysAndBookings[dateIndex].bookedTimings;
    //         for (let i = 0; i < bookedTimings.length; i++) {
    //             const start = moment(bookedTimings[i].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset);
    //             const end = moment(bookedTimings[i].to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset);
    //             const time = moment(slot.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).add(5, 'minutes');
    //             if (time.isBetween(start, end) && bookedTimings[i].lockedBy != user.email)
    //                 return false;
    //         }
    //         return true;
    //     } catch (err) {
    //         Alert.error("Some error occured, please try again later or report support team.", {
    //             position: 'top-right',
    //             effect: 'slide'
    //         });
    //         GeneralService.logError('at Client BookSession isAvailableTimeSlot', 'Error in try catch block', err.stack);
    //     }
    // }

    // const getDateAvailability = () => {

    //     let index =
    //         listOfAvailableDaysAndBookings
    //             .findIndex(x =>
    //                 moment(x.date).format("DD-MM-YYYY")
    //                 ===
    //                 moment(selectedDate).utcOffset("+05:30").format("DD-MM-YYYY")
    //             );

    //     // let index = listOfAvailableDaysAndBookings.findIndex(x => x.date === moment(selectedDate).utcOffset(0).set({hour:0, minute:0, second:0, millisecond:0}).toISOString());
    //     // console.log("index", index);
    //     // console.log("one", moment(listOfAvailableDaysAndBookings[0].date).utcOffset(0).format("DD-MM-YYYY"));
    //     // console.log("other1", moment(selectedDate).utcOffset(0).format("DD-MM-YYYY"));
    //     if (index > -1) {
    //         return listOfAvailableDaysAndBookings[index].availableStatus
    //     } else {
    //         return false;
    //     }
    // }

    // const getSelectedDateSlots = () => {
    //     let startOfDay = moment(selectedDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).startOf('date');
    //     let endOfDay = moment(selectedDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).endOf('date');;
    //     console.log("selectedTimeZone", selectedTimeZone.offset, "startOfDay", startOfDay.format("DD-MM-YYYY HH:mm"), "endOfDay", endOfDay.format("DD-MM-YYYY HH:mm"));
    //     let slots = [];
    //     listOfAvailableDaysAndBookings.map(item => {
    //         console.log(item.from && moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).isBetween(startOfDay, endOfDay));
    //         if (item.from && moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).isBetween(startOfDay, endOfDay) && moment(item.to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).isBetween(startOfDay, endOfDay)) {
    //             slots.push(item);
    //         }
    //     })
    //     return slots;
    // }

    const createSlots = (list) => {
        let slots = [];
        let dateSlots = [];
        let rangeIndex = 1;
        dateSlots.push({ isExtra: true, value: rangeIndex });
        let date = "";
        Promise.all(
            list.map((item, index) => {
                // if (item.availableStatus && item.from) {
                if (date === "") {
                    date = moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY");
                } else if (date !== moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY")) {
                    slots.push(dateSlots);
                    date = "";
                    dateSlots = [];
                    rangeIndex = 0;
                } else if (index === (list.length - 1)) {
                    slots.push(dateSlots);
                }
                if (index > 0 && moment(list[index - 1].to).diff(moment(item.from), 'minute') !== 0) {
                    rangeIndex = rangeIndex + 1;
                    dateSlots.push({ isExtra: true, value: rangeIndex });
                }
                dateSlots.push(item);
                // }
            })
        ).then(() => {
            setListOfDateAndSlots(slots);
        })
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.guideTakenFor.includes("BookSession")) {
                AuthService.markVisited("BookSession");
                setUser({ ...user, guideTakenFor: [...user.guideTakenFor, "BookSession"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        loading ?
            <FullPageLoader /> :
            <div id="bookSessionDiv">
                <ErrorBoundary errorAt="At Client BookSession Joyride">
                    <Joyride
                        callback={handleJoyrideCallback}
                        continuous={true}
                        getHelpers={getHelpers}
                        run={tutorial.status}
                        scrollToFirstStep={true}
                        showProgress={true}
                        showSkipButton={true}
                        steps={tutorial.steps}
                        locale={{ last: 'Finish' }}
                        styles={{
                            options: {
                                primaryColor: "rgb(56, 78, 115)",
                                zIndex: 10000,
                            },
                        }}
                    />
                </ErrorBoundary>
                <ErrorBoundary errorAt="At Client BookSession ClientNavbar">
                    <ProviderNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
                </ErrorBoundary>
                <div className="p-3">

                    <div>
                        <div className="d-flex">
                            <h1 id="booksession-heading" style={theme.headingBold}>Reserve Session</h1>
                        </div>
                        <hr style={{ borderWidth: "2px" }} />
                    </div>

                    {profileComplete.status ? null : <Message message={profileComplete.message} />}

                    {isFirstSession.status ? <Message message={isFirstSession.message} /> : null}

                    <div className="p-3">
                        <h2 id="book-session-selected-client-label" style={theme.subHeadingSemibold}>Selected Client</h2>
                        <hr />

                        <Input className="mb-3" type="select" name="selectedClient" id="selectedClient" style={theme.formInput} onChange={(e) => handleSelectClient(e.target.value)} defaultValue={0} value={selectedClientIndex} required>
                            <option value={0}>Select one</option>
                            {
                                clients.map((item, index) => (
                                    <option key={index} value={index + 1}>{item.clientId.email}</option>
                                ))
                            }
                        </Input>
                        <ErrorBoundary errorAt={`At Provider BookSession SelectedClient`}>
                            <SelectedClient client={selectedClientIndex === 0 ? null : clients[selectedClientIndex - 1]} />
                        </ErrorBoundary>
                    </div>

                    <div className="p-3">
                        <h2 id="book-session-select-date-label" style={theme.subHeadingSemibold}>Select Date</h2>
                        <hr />

                        {
                            listOfAvailableDaysAndBookings.length ?
                                <div>
                                    <div className="row px-3">
                                        <div id="selectDate">
                                            <ErrorBoundary errorAt="At Client BookSession Calendar">
                                                <Calendar
                                                    className="m-2 rounded"
                                                    onChange={handleDatePickerChange}
                                                    value={selectedDate}
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                />
                                            </ErrorBoundary>
                                        </div>
                                    </div>
                                    <br />

                                    <div className="mt-4">
                                        <h3 style={theme.h3headingSemibold}>{isFirstSession.status ? "Select Time Slot" : "Select Time Slot(s) -- Maximum 2 from any one Time Range"}</h3>
                                        <ErrorBoundary errorAt="At Client BookSession SelectTimeSlots">
                                            <div id="slotList" className="row px-3 justify-content-md-start justify-content-sm-between justify-content-between">
                                                {
                                                    selectedSlotIndex > -1 && listOfDateAndSlots[selectedSlotIndex].length > 0
                                                        ?
                                                        listOfDateAndSlots[selectedSlotIndex].map((item, index) => (
                                                            item.isExtra ?
                                                                <div key={index} id={`slot${index}`} className="col-12 mt-3" style={theme.extraSlotCard}>
                                                                    <span><span style={theme.paragraphMedium}>Time Range -</span> {item.value}</span>
                                                                    <hr className="p-0 mt-0 mb-1" />
                                                                </div>
                                                                :
                                                                item.availableStatus && !item.isLocked && !item.isBooked ?
                                                                    <div key={index} id={`slot${index}`} className="col-sm-5 col-5 col-md-2 m-2 text-center py-5" style={selectedTimeSlot.includes(index) ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectTimeSlot(index)}>
                                                                        <span>{`${moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")} - ${moment(item.to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")}`}</span>
                                                                    </div>
                                                                    :
                                                                    <div key={index} id={`slot${index}`} className="col-sm-5 col-5 col-md-2 m-2 text-center py-5" style={theme.disabledSlotCard}>
                                                                        <span>{`${moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")} - ${moment(item.to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")}`}</span>
                                                                    </div>
                                                        ))
                                                        :
                                                        <h3>No slots available on this day</h3>
                                                }
                                                <div className="col-12 py-2 mt-2 mb-2" style={theme.extraSlotCard}>
                                                    {/* <span>{item.value}</span> */}
                                                </div>
                                            </div>
                                        </ErrorBoundary>
                                    </div>

                                    <ErrorBoundary errorAt="At Client BookSession SelectMode">
                                        <div className="mt-4">
                                            <h3 style={theme.h3headingSemibold}>Select Mode of Service</h3>
                                            <div id="selectMode" className="row px-3">
                                                <div id="callService" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == providerDetail.modeOfService.call ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(providerDetail.modeOfService.call)}>
                                                    <label style={theme.subHeadingSemibold}>{providerDetail.modeOfService.call.name}</label>
                                                    <hr className="mt-0" />
                                                    {
                                                        clientSubscription.isCallSessionAvailable ?
                                                            <label>Included under subscription</label>
                                                            :
                                                            isFirstSession.status
                                                                ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                : <label>Rate - ₹{selectedTimeSlot.length === 2 ? providerDetail.modeOfService.call.rate * 2 : providerDetail.modeOfService.call.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                    }


                                                </div>
                                                {
                                                    isFirstSession.status ?
                                                        null :
                                                        [
                                                            <div key="chat" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == providerDetail.modeOfService.chat ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(providerDetail.modeOfService.chat)}>
                                                                <label style={theme.subHeadingSemibold}>{providerDetail.modeOfService.chat.name}</label>
                                                                <hr className="mt-0" />
                                                                {
                                                                    clientSubscription.isActive ?
                                                                        <label>Included under subscription</label>
                                                                        :
                                                                        isFirstSession.status
                                                                            ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                            : <label>Rate - ₹{selectedTimeSlot.length === 2 ? providerDetail.modeOfService.chat.rate * 2 : providerDetail.modeOfService.chat.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                }
                                                            </div>,
                                                            <div key="video" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == providerDetail.modeOfService.video ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(providerDetail.modeOfService.video)}>
                                                                <label style={theme.subHeadingSemibold}>{providerDetail.modeOfService.video.name}</label>
                                                                <hr className="mt-0" />
                                                                {
                                                                    clientSubscription.isVideoSessionAvailable ?
                                                                        <label>Included under subscription</label>
                                                                        :
                                                                        isFirstSession.status
                                                                            ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                            : <label>Rate - ₹{selectedTimeSlot.length === 2 ? providerDetail.modeOfService.video.rate * 2 : providerDetail.modeOfService.video.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                }
                                                            </div>,
                                                            <div key="inPerson" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == providerDetail.modeOfService.inPerson ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(providerDetail.modeOfService.inPerson)}>
                                                                <label style={theme.subHeadingSemibold}>{providerDetail.modeOfService.inPerson.name}</label>
                                                                <hr className="mt-0" />
                                                                {
                                                                    clientSubscription.isInPersonSessionAvailable ?
                                                                        <label>Included under subscription</label>
                                                                        :
                                                                        isFirstSession.status
                                                                            ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                            : <label>Rate - ₹{selectedTimeSlot.length === 2 ? providerDetail.modeOfService.inPerson.rate * 2 : providerDetail.modeOfService.inPerson.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                }
                                                            </div>
                                                        ]
                                                }
                                            </div>
                                        </div>
                                    </ErrorBoundary>

                                    {
                                        isFirstSession.status ? null :
                                            <div className="mt-4">
                                                <ErrorBoundary errorAt="At Client BookSession IncludeReport">
                                                    <h3 style={theme.h3headingSemibold}>Include report</h3>
                                                    {
                                                        providerDetail.isReportIncluded
                                                            ?
                                                            providerDetail.isReportIncluded ? <span>Report is included</span> : <span>Report is not included</span>
                                                            :
                                                            <div className="row px-3">
                                                                <div id="sessionReport" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={includeReport ? theme.selectedSlotCard : theme.slotCard} onClick={toggleIncludeReport}>
                                                                    <label style={theme.subHeadingSemibold}>Session report</label>
                                                                    <hr className="mt-0" />
                                                                    {
                                                                        clientSubscription.reportStatus ?
                                                                            <label>Included under subscription</label>
                                                                            :
                                                                            <label> Rate - ₹{providerDetail.reportCharge}</label>
                                                                    }
                                                                </div>
                                                            </div>
                                                    }
                                                </ErrorBoundary>
                                            </div>
                                    }
                                </div>
                                :
                                <h3>No dates available for selected counsellor</h3>
                        }
                    </div>

                    {
                        listOfAvailableDaysAndBookings.length ?
                            <div className="mt-5">
                                <ErrorBoundary errorAt="At Client BookSession GSTCharge">
                                    <p style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm }}>*{providerDetail.GSTCharge}% GST will be applicable on total amount</p>
                                    <button className="btn" style={theme.button} disabled={disableSubmitBtn} onClick={handleSubmit} id="saveBtn">{disableSubmitBtn ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Proceed to Payment</button>
                                </ErrorBoundary>
                            </div>
                            : null
                    }


                </div >
            </div >
    )

}

export default ReserveSession;